import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { getSchemaBySlug } from '../engine/registry';
import { submitFinal } from '../engine/persistence';
import { trackEvent, identifyContact } from '../engine/analytics';
import IntakeForm from '../components/intake/IntakeForm';
import NotFound from './NotFound';

const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string) ?? '';

export default function AssessmentIntake() {
  const { slug = '' } = useParams();
  const schema = getSchemaBySlug(slug);
  const navigate = useNavigate();
  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!schema) return <NotFound />;

  const uuid = sessionStorage.getItem(`assessment:${schema.id}:uuid`) ?? '';
  const startedAt = sessionStorage.getItem(`assessment:${schema.id}:startedAt`) ?? '';
  const answers = JSON.parse(sessionStorage.getItem(`assessment:${schema.id}:answers`) ?? '{}');

  const onSubmit = async (intake: {
    email: string;
    name?: string;
    company?: string;
    role?: string;
    phone?: string;
  }) => {
    if (!turnstileToken) {
      setError('Please complete the verification widget above the button.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await submitFinal({
        uuid,
        schemaId: schema.id,
        schemaVersion: schema.version,
        startedAt,
        answers,
        intake,
        turnstileToken,
      });
      identifyContact(intake.email, { schema_id: schema.id });
      trackEvent(`${schema.analytics.eventPrefix}_intake_submitted`, {
        schema_id: schema.id,
        uuid: resp.uuid,
      });
      sessionStorage.clear();
      navigate(`/${schema.slug}/result/${resp.uuid}/`);
    } catch (e) {
      const message = (e as Error).message;
      // Turnstile tokens are single-use; if the submit failed (any reason),
      // reset the widget so the user can get a fresh token on the next try.
      if (turnstileRef.current) {
        turnstileRef.current.reset();
        setTurnstileToken('');
      }
      setError(
        message.includes('turnstile')
          ? 'Verification expired or failed. Please tick the box again and resubmit.'
          : message,
      );
      setSubmitting(false);
    }
  };

  return (
    <article className="intake">
      <h1 className="intake__title">Almost there</h1>
      <p className="intake__lede">
        Enter your details to unlock your personalised report. It will arrive in your inbox within
        60 seconds.
      </p>
      <IntakeForm
        fields={schema.intakeFields}
        submitting={submitting}
        onSubmit={onSubmit}
        turnstileSlot={
          SITE_KEY ? (
            <div className="intake__turnstile">
              <Turnstile
                ref={turnstileRef}
                siteKey={SITE_KEY}
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken('')}
                onError={() => setTurnstileToken('')}
                options={{ theme: 'auto', appearance: 'always', size: 'normal' }}
              />
            </div>
          ) : (
            <p className="text-sm text-warning">Turnstile site key missing — submissions will fail.</p>
          )
        }
      />
      {error && <p className="intake__error">{error}</p>}
    </article>
  );
}
